import React, { useCallback } from 'react';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useEditorContext } from '../SMLEditorContext';
import { CatalogLinksForm } from '../catalogLinks/CatalogLinksForm';
import { useBlockAutosave } from './useBlockAutosave';
import { SMLBlockFormFoot } from './SMLBlockFormFoot';
import { formMessages } from './formMessages';
import { TextBlockForm } from './TextBlockForm';
import { LinkBlockForm } from './LinkBlockForm';
import { PlayerBlockForm } from './PlayerBlockForm';
import { MediaBlockForm } from './MediaBlockForm';
import { SocialBlockForm } from './SocialBlockForm';
import { ProfileBlockForm } from './ProfileBlockForm';
import { SubscribeBlockForm } from './SubscribeBlockForm';
import { ImageBlockForm } from './ImageBlockForm';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLBlockFormProps {
  widget: SerializedWidget;
  /** Every block of the page, to find a new block in an answer ("Turn into Media"). */
  pageWidgets: readonly SerializedWidget[];
}

/**
 * The form of an open row (design d0): one column of fields that save by themselves, and the foot
 * with the save state, Delete and Done. Done sends a change that waits and closes the row; with a
 * wrong field the row stays open.
 */
const SMLBlockFormComponent: React.FC<SMLBlockFormProps> = ({ widget, pageWidgets }) => {
  const { accountId, smartLinkId, url, requestDelete } = useEditorContext();
  const autosave = useBlockAutosave({ accountId, smartLinkId, widget });
  const { flush, status } = autosave;
  const onDone = useCallback(() => {
    if (status === 'invalid') {
      return;
    }
    flush();
    url.closeLayer();
  }, [flush, status, url]);
  const onDelete = useCallback(() => requestDelete(widget), [requestDelete, widget]);
  const onReplaced = useCallback(
    (widgetUUID: string | null) => (widgetUUID === null ? url.closeLayer() : url.openBlock(widgetUUID)),
    [url]
  );

  let form: React.ReactNode = null;
  switch (widget.type) {
    case SmartLinkWidgetType.info:
      form = <TextBlockForm widget={widget} autosave={autosave} />;
      break;
    case SmartLinkWidgetType.link:
      form = <LinkBlockForm widget={widget} accountId={accountId} smartLinkId={smartLinkId} autosave={autosave} />;
      break;
    case SmartLinkWidgetType.player:
      form = <PlayerBlockForm widget={widget} accountId={accountId} autosave={autosave} />;
      break;
    case SmartLinkWidgetType.media:
      form = (
        <MediaBlockForm
          widget={widget}
          accountId={accountId}
          smartLinkId={smartLinkId}
          autosave={autosave}
          onDelete={onDelete}
        />
      );
      break;
    case SmartLinkWidgetType.socialLinks:
      form = <SocialBlockForm widget={widget} autosave={autosave} />;
      break;
    case SmartLinkWidgetType.profile:
      form = <ProfileBlockForm />;
      break;
    case SmartLinkWidgetType.presave:
      form = <CatalogLinksForm widget={widget} accountId={accountId} smartLinkId={smartLinkId} autosave={autosave} />;
      break;
    case SmartLinkWidgetType.presaveSubscribe:
      form = <SubscribeBlockForm widget={widget} autosave={autosave} />;
      break;
    case SmartLinkWidgetType.image:
      form = (
        <ImageBlockForm
          widget={widget}
          accountId={accountId}
          smartLinkId={smartLinkId}
          autosave={autosave}
          pageWidgets={pageWidgets}
          onReplaced={onReplaced}
        />
      );
      break;
    default:
      form = null;
  }

  const isAddressForm = widget.type === SmartLinkWidgetType.link || widget.type === SmartLinkWidgetType.player;
  return (
    <div className={styles.form}>
      {form}
      <SMLBlockFormFoot
        status={status}
        isLive={autosave.isLive}
        invalidMessage={isAddressForm ? formMessages.notSavedAddress : formMessages.notSaved}
        onDelete={onDelete}
        onDone={onDone}
      />
    </div>
  );
};

export const SMLBlockForm = TypedMemo(SMLBlockFormComponent);
